/**
 * Mobile Detection Utilities
 * Provides consistent mobile platform detection across apps
 */

export interface MobileDetectionResult {
  isMobile: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  isIOSPWA: boolean;
  isCapacitor: boolean;
  platform: "web" | "android" | "ios";
  userAgent: string;
}

/**
 * Comprehensive mobile detection
 */
export function detectMobile(): MobileDetectionResult {
  const userAgent = navigator.userAgent.toLowerCase();

  // Detect iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Detect Android devices
  const isAndroid = /android/.test(userAgent);

  // Detect if running as installed PWA on iOS
  const isIOSPWA =
    isIOS &&
    ((window.navigator as any).standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches);

  // Detect Capacitor (native app wrapper)
  const isCapacitor = !!(window as any).Capacitor;

  // Enhanced mobile detection - check for desktop browsers explicitly
  const isDesktopBrowser =
    /windows nt|macintosh|mac os x|linux x86_64/.test(userAgent) &&
    !/mobile|android|iphone|ipad|ipod/.test(userAgent);

  // Overall mobile detection - exclude desktop browsers
  const isMobile = (isIOS || isAndroid || isCapacitor) && !isDesktopBrowser;

  // Determine platform
  let platform: "web" | "android" | "ios" = "web";
  if (isCapacitor) {
    // Use Capacitor's platform detection if available
    try {
      const { Capacitor } = window as any;
      platform = Capacitor.getPlatform();
    } catch {
      platform = isAndroid ? "android" : isIOS ? "ios" : "web";
    }
  } else if (isAndroid) {
    platform = "android";
  } else if (isIOS) {
    platform = "ios";
  }

  return {
    isMobile,
    isAndroid,
    isIOS,
    isIOSPWA,
    isCapacitor,
    platform,
    userAgent,
  };
}

/**
 * Check if PWA updates should be shown
 * CRITICAL: Only show when running AS an installed PWA
 * NEVER show in browser mode (even on mobile) or on desktop
 */
export function shouldShowPWAUpdates(): boolean {
  const detection = detectMobile();

  // Explicit desktop check - NEVER show on desktop
  const userAgent = navigator.userAgent.toLowerCase();
  const isDesktop =
    (/windows nt|macintosh|mac os x|linux x86_64/.test(userAgent) &&
      !/mobile|android|iphone|ipad|ipod/.test(userAgent)) ||
    window.innerWidth > 1024; // Screen width check as additional safety

  if (isDesktop) {
    return false;
  }

  // NEVER show PWA updates on desktop
  if (!detection.isMobile && !detection.isCapacitor) {
    return false;
  }

  // CRITICAL: Only show when running AS an installed PWA, not in browser
  const isRunningAsPWA =
    (window.navigator as any).standalone === true || // iOS PWA (standalone mode)
    window.matchMedia("(display-mode: standalone)").matches || // Android/iOS PWA
    window.matchMedia("(display-mode: fullscreen)").matches || // Fullscreen PWA
    window.matchMedia("(display-mode: minimal-ui)").matches || // Minimal UI PWA
    detection.isCapacitor; // Capacitor native app

  // Only return true if BOTH mobile AND running as PWA
  return detection.isMobile && isRunningAsPWA;
}

/**
 * Get platform-specific update configuration
 */
export function getPlatformUpdateConfig() {
  const detection = detectMobile();

  if (detection.isIOS) {
    return {
      checkInterval: detection.isIOSPWA ? 2 * 60 * 1000 : 5 * 60 * 1000, // 2 min for PWA, 5 min for Safari
      backgroundCheckInterval: detection.isIOSPWA ? 15 * 1000 : 30 * 1000, // 15s for PWA, 30s for Safari
      autoInstallDelay: 2 * 60 * 1000, // 2 minutes for iOS
      notificationDelay: detection.isIOSPWA ? 100 : 500, // Almost immediate for PWA, 0.5s for Safari
      useAggressiveChecking: true,
      cacheBusting: true,
    };
  } else if (detection.isAndroid) {
    return {
      checkInterval: 30 * 60 * 1000, // 30 minutes for Android
      backgroundCheckInterval: 5 * 60 * 1000, // 5 minutes for Android
      autoInstallDelay: 10 * 60 * 1000, // 10 minutes for Android
      notificationDelay: 2000, // 2 seconds for Android
      useAggressiveChecking: false,
      cacheBusting: false,
    };
  } else {
    // Web/desktop - minimal PWA update checking
    return {
      checkInterval: 60 * 60 * 1000, // 1 hour for web
      backgroundCheckInterval: 15 * 60 * 1000, // 15 minutes for web
      autoInstallDelay: 30 * 60 * 1000, // 30 minutes for web
      notificationDelay: 3000, // 3 seconds for web
      useAggressiveChecking: false,
      cacheBusting: false,
    };
  }
}

/**
 * Check if device supports service workers
 */
export function supportsServiceWorkers(): boolean {
  return "serviceWorker" in navigator;
}

/**
 * Check if device supports push notifications
 */
export function supportsPushNotifications(): boolean {
  return "Notification" in window && "serviceWorker" in navigator;
}

/**
 * Get user-friendly platform name
 */
export function getPlatformName(): string {
  const detection = detectMobile();

  if (detection.isCapacitor) {
    return detection.platform === "android"
      ? "Android App"
      : detection.platform === "ios"
      ? "iOS App"
      : "Mobile App";
  } else if (detection.isIOSPWA) {
    return "iOS PWA";
  } else if (detection.isIOS) {
    return "iOS Safari";
  } else if (detection.isAndroid) {
    return "Android Browser";
  } else {
    return "Web Browser";
  }
}
